

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { Avatar, InputAdornment, Tooltip, Typography } from '@mui/material';
import { MdOutlineLink, MdOutlineLinkOff } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../../../core/hooks/db/user/useUser';
import { useRole } from '../../../../core/hooks/db/role/useRole';

import { useIdentityStore } from '../../../../core/store';
import { TableNamesType } from '../../../../core/interfaces';
import { Util } from '../../../../core/util/util';
import { useCategory } from '../../../../core/hooks/db/fisioteduca/useFisioteduca';

// Selector de Hook según la tabla
const ChooseHook = (table: TableNamesType) => {
    const userHook = useUser();
    const roleHook = useRole();
    const categoryHook = useCategory();


    switch (table) {
        case 'user':
            return userHook;
        case 'role':
            return roleHook;
        case 'category': {
            return categoryHook;
        }

        default:
            return null;
    }
};

// Procesamiento de datos
const fillOutObject = async (table: TableNamesType, rows: any[], defaultRow, defaultRowMultiple) => {
    const body = rows.map((row) => {

        // console.log("mira que es row", table, row);
        switch (table) {
            case 'user':
                return { ...row, text: Util.capitalize(row.email), value: row.id };
            case "users-for-calendar" as any:
                return { ...row, showWhoIam: true, urlAvatar: row?.image ? `${process.env.REACT_APP_API_URL_MS_AUTH}${row.image}` : "", text: Util.capitalize(row.name) + " " + Util.capitalize(row.lastName), value: row.id };
            case "role":
                let title = "";
                if (row.roleType === "ROLE_ADMIN") {
                    title = "Administrador";
                } else if (row.roleType === "ROLE_USER") {
                    title = "Usuario";
                } else if (row.roleType === "ROLE_DEVELOPER") {
                    title = "Desarrollador";
                } else if (row.roleType === "ROLE_MANAGER") {
                    title = "Manager";
                }
                else if (row.roleType === "ROLE_SUPPORT") {
                    title = "Soporte";
                }
                if (title && title !== "") {
                    return { ...row, text: title, value: row.roleType };
                }
                break;
            case "category":
                return { ...row, text: Util.capitalize(row.name), value: row.id };

            default:
                return row;
        }
    });
    return body;
};

// Props del componente
interface AutocompleteProps {
    defaultData?: any[];
    multiselect?: boolean;
    placeholder?: string;
    disabled?: boolean;
    hasAvatar?: boolean;
    hasLink?: boolean;
    id?: string;
    name: string;
    defaultValue?: { key: string; value: any };
    defaultValueMultiple?: { key: string; values: any[] };
    table: TableNamesType;
    error?: string;
    isRequired?: boolean;
    onChange: (newValue: any) => void;
    onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;

}

// Componente principal
export const AutocompleteMui = React.memo((props: AutocompleteProps) => {
    const {
        defaultData,
        placeholder,
        disabled,
        hasAvatar = false,
        hasLink = false,
        id,
        name,
        defaultValue,
        defaultValueMultiple,
        table,
        onChange,
        multiselect = false,
        error = '',
        isRequired = false,
    } = props;

    const { t } = useTranslation();
    const { autocomplete, autocompleteForCalendar, getEstablishment } = ChooseHook(table) as any || {};
    const [initialized, setInitialized] = useState(false);




    const { identity } = useIdentityStore();

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedValue, setSelectedValue] = useState<any>(multiselect ? [] : null);

    // Memoizar datos iniciales
    const memoizedDefaultData = useMemo(() => defaultData, [defaultData]);
    const memoizedDefaultValue = useMemo(() => defaultValue, [defaultValue]);
    const memoizedDefaultValueMultiple = useMemo(() => defaultValueMultiple, [defaultValueMultiple]);

    // Fetch de datos y procesamiento
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            let transformedData;
            if (memoizedDefaultData) {
                transformedData = await fillOutObject(
                    table,
                    memoizedDefaultData,
                    multiselect ? null : memoizedDefaultValue,
                    multiselect ? memoizedDefaultValueMultiple : null
                );
            } else {
                let response;
                // if (table === 'users-for-calendar' as any) {
                //     response = await autocompleteForCalendar();
                // } else if (table === 'establishments') {
                //     response = await getEstablishment({
                //         page: 1,
                //         itemsPerPage: 1000,
                //         filters: { idCompanyFk: { value: identity?.idCompanySelected } },
                //     });
                //     response = {
                //         ok: response.ok,
                //         item: response.item.rows,
                //     };

                // } else {
                response = await autocomplete();
                // }

                if (response?.ok) {
                    transformedData = await fillOutObject(
                        table,
                        response.item,
                        multiselect ? null : memoizedDefaultValue,
                        multiselect ? memoizedDefaultValueMultiple : null
                    );
                }

            }

            setOptions(transformedData || []);
            setLoading(false);

            // Configurar valores por defecto
            if (!initialized && memoizedDefaultValue) {
                const defaultOption = transformedData?.find(option => option?.value == memoizedDefaultValue?.value);
                setSelectedValue(multiselect ? [defaultOption] : defaultOption || null);
                setInitialized(true);

            } else if (memoizedDefaultValueMultiple && multiselect) {
                const defaultOptions = transformedData?.filter(option =>
                    memoizedDefaultValueMultiple.values.includes(option.value)
                );
                setSelectedValue(defaultOptions || []);
                setInitialized(true);
            }
        };

        fetchData();
    }, [memoizedDefaultData, table, memoizedDefaultValue, memoizedDefaultValueMultiple, multiselect, identity?.idCompanySelected]);

    // Cambio de selección
    const handleChange = useCallback((event, newValue) => {
        setSelectedValue(newValue);
        if (multiselect) {
            onChange(newValue);
        } else {
            onChange(newValue || null);
        }
    }, [multiselect, onChange]);

    return (
        <Autocomplete

            noOptionsText={t('common.noOptions')}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            id={id}
            disabled={disabled}
            options={options}
            getOptionLabel={(option) => option?.text || ''}
            loading={loading}
            onChange={handleChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    // label=
                    label={
                        <>
                            {placeholder} {isRequired && <Typography component={'span'} color="error">*</Typography>}
                        </>
                    }
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                {hasAvatar && !multiselect && selectedValue && (
                                    <InputAdornment position="start">
                                        <Avatar
                                            src={selectedValue?.urlAvatar || ''}
                                            alt={selectedValue?.text}
                                            sx={{ width: 24, height: 24, marginRight: 1 }}
                                        >
                                            {!selectedValue?.urlAvatar && selectedValue?.text.charAt(0).toUpperCase()}
                                        </Avatar>
                                    </InputAdornment>
                                )}
                                {hasLink && !multiselect && selectedValue && (
                                    <InputAdornment position="start">
                                        {selectedValue?.isActived ? (
                                            <Tooltip title={t('autocomplete.establishment.botOn')}>
                                                <MdOutlineLink style={{ color: 'green', fontSize: 18 }} />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title={t('autocomplete.establishment.botOff')}>
                                                <MdOutlineLinkOff style={{ color: 'red', fontSize: 18 }} />
                                            </Tooltip>
                                        )}
                                    </InputAdornment>
                                )}
                                {params.InputProps.startAdornment}
                            </>
                        ),
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    error={!!error}
                    helperText={error}
                />
            )}
            renderOption={(props, option) => (
                <li {...props}>
                    {hasAvatar && (
                        <Avatar
                            src={option.urlAvatar || ''}
                            alt={option.text}
                            sx={{ width: 24, height: 24, marginRight: 1 }}
                        >
                            {!option?.urlAvatar && option?.text.charAt(0).toUpperCase()}
                        </Avatar>
                    )}
                    {hasLink && (
                        <InputAdornment position="start">
                            {option?.isActived ? (
                                <MdOutlineLink style={{ color: 'green', fontSize: 18 }} />
                            ) : (
                                <MdOutlineLinkOff style={{ color: 'red', fontSize: 18 }} />
                            )}
                        </InputAdornment>
                    )}
                    {option?.text}
                </li>
            )}
            value={selectedValue}
            isOptionEqualToValue={(option, value) => option.value == value?.value}
            multiple={multiselect}
        />
    );
});


AutocompleteMui.displayName = 'AutocompleteMui';

